import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { cn } from "@/lib/utils";
import bgMusicAsset from "@/assets/Mantra-ki-Mahima.mp3";

/**
 * Looping ambient background music for the public site (mounted in SiteLayout,
 * so it's absent on admin and persists across user-page navigation).
 *
 * Source: the bundled track (`assets/Mantra-ki-Mahima.mp3`), or override with
 * `VITE_JL_BG_MUSIC_URL` (CDN/URL). Browsers block autoplay-with-sound, so we
 * try to play on load and, if blocked, start on the visitor's first
 * interaction. The toggle reflects the audio's real state, and the visitor's
 * pause choice is remembered.
 */
const MUSIC_URL =
  ((import.meta.env.VITE_JL_BG_MUSIC_URL as string | undefined) ?? "").trim() ||
  bgMusicAsset;

const STORAGE_KEY = "jl-bg-music-paused-v2";
const VOLUME = 0.3;
const GESTURE_EVENTS = ["pointerdown", "keydown", "touchstart", "scroll"];

export function BackgroundMusic() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Whether the visitor wants music (default yes; respects a saved pause).
  const wantsMusicRef = useRef<boolean>(true);
  if (typeof window !== "undefined") {
    // Read once, synchronously, before the autostart effect.
    try {
      wantsMusicRef.current = localStorage.getItem(STORAGE_KEY) !== "1";
    } catch {
      wantsMusicRef.current = true;
    }
  }

  // Reflect the element's real play/pause state in the button.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    audio.volume = VOLUME;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
    };
  }, []);

  // Autostart on load; if the browser blocks it, start on first interaction.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !wantsMusicRef.current) {
      return;
    }
    let removeGestureListeners: (() => void) | null = null;
    const result = audio.play();
    if (result && typeof result.catch === "function") {
      result.catch(() => {
        const onGesture = () => {
          if (wantsMusicRef.current) {
            audio.play().catch(() => undefined);
          }
          removeGestureListeners?.();
        };
        GESTURE_EVENTS.forEach(evt =>
          window.addEventListener(evt, onGesture, { passive: true })
        );
        removeGestureListeners = () => {
          GESTURE_EVENTS.forEach(evt =>
            window.removeEventListener(evt, onGesture)
          );
          removeGestureListeners = null;
        };
      });
    }
    return () => removeGestureListeners?.();
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    // Drive off the real state so a click always does the obvious thing.
    if (audio.paused) {
      wantsMusicRef.current = true;
      try {
        localStorage.setItem(STORAGE_KEY, "0");
      } catch {
        /* ignore */
      }
      audio.play().catch(() => undefined);
    } else {
      wantsMusicRef.current = false;
      try {
        localStorage.setItem(STORAGE_KEY, "1");
      } catch {
        /* ignore */
      }
      audio.pause();
    }
  };

  return (
    <>
      <audio ref={audioRef} src={MUSIC_URL} loop preload="auto" />
      <button
        type="button"
        onClick={toggle}
        aria-label={
          isPlaying ? "Pause background music" : "Play background music"
        }
        title={isPlaying ? "Pause music" : "Play music"}
        className={cn(
          "fixed bottom-5 right-5 z-50 flex h-11 w-11 items-center justify-center rounded-full border shadow-lg backdrop-blur transition-colors",
          isPlaying
            ? "border-primary/30 bg-primary/90 text-white hover:bg-primary"
            : "border-border/60 bg-white/90 text-muted-foreground hover:bg-white"
        )}
      >
        {isPlaying ? (
          <Volume2 className="h-5 w-5 animate-pulse" />
        ) : (
          <VolumeX className="h-5 w-5" />
        )}
      </button>
    </>
  );
}
